import { useRef, useState, useCallback, useEffect } from 'react';
import { toPng } from 'html-to-image';
import { QRCodeSVG } from 'qrcode.react';
import { Download, X, Share2, Link2, Check } from 'lucide-react';

interface SharePosterProps {
  storyTitle: string;
  storyCover: string | null;
  sharerNickname: string;
  sharerAvatar: string | null;
  shareUrl: string;
  onClose: () => void;
}

const APP_URL = import.meta.env.VITE_APP_URL || window.location.origin;

async function convertImageToDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('图片转 base64 失败:', url, err);
    return null;
  }
}

export default function SharePoster({
  storyTitle,
  storyCover,
  sharerNickname,
  sharerAvatar,
  shareUrl,
  onClose,
}: SharePosterProps) {
  const posterRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [coverDataUrl, setCoverDataUrl] = useState<string | null>(null);
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);
  const [coverError, setCoverError] = useState(false);
  const [copied, setCopied] = useState(false);

  // 预先将跨域图片转换为 data URL，避免 html-to-image 的 CORS 问题
  useEffect(() => {
    if (storyCover && storyCover.startsWith('http')) {
      convertImageToDataUrl(storyCover).then(setCoverDataUrl);
    } else {
      setCoverDataUrl(storyCover);
    }
    if (sharerAvatar && sharerAvatar.startsWith('http')) {
      convertImageToDataUrl(sharerAvatar).then(setAvatarDataUrl);
    } else {
      setAvatarDataUrl(sharerAvatar);
    }
  }, [storyCover, sharerAvatar]);

  const generatePoster = useCallback(async () => {
    if (!posterRef.current) return null;
    setIsGenerating(true);
    try {
      // 等待海报内所有图片加载完成（加载失败也继续）
      const imgs = posterRef.current.querySelectorAll('img');
      await Promise.all(
        Array.from(imgs).map(
          (img) =>
            new Promise<void>((resolve) => {
              if (img.complete) {
                resolve();
              } else {
                img.onload = () => resolve();
                img.onerror = () => resolve();
              }
            })
        )
      );

      const dataUrl = await toPng(posterRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        cacheBust: true,
        skipFonts: true, // 跳过跨域字体内联，避免 SecurityError
      });
      return dataUrl;
    } catch (err) {
      console.error('生成海报失败:', err);
      alert('生成海报失败，请重试');
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const handleDownload = async () => {
    const dataUrl = await generatePoster();
    if (!dataUrl) return;

    const link = document.createElement('a');
    link.download = `绘本分享-${storyTitle}.png`;
    link.href = dataUrl;
    link.click();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullShareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 降级方案
      const input = document.createElement('input');
      input.value = fullShareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const fullShareUrl = `${APP_URL}${shareUrl}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-500" />
            <h3 className="font-bold text-slate-800">分享绘本</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-slate-100 transition"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* 海报预览 */}
        <div className="p-4 flex justify-center bg-slate-50">
          <div
            ref={posterRef}
            className="w-[280px] rounded-2xl overflow-hidden shadow-lg"
            style={{
              background: 'linear-gradient(180deg, #4F8EF7 0%, #6BB5FF 40%, #A8D4FF 100%)',
            }}
          >
            {/* 顶部装饰 */}
            <div className="relative px-4 pt-5 pb-3">
              {/* 星星装饰 */}
              <div className="absolute top-3 right-4 text-amber-300 text-lg">⭐</div>
              <div className="absolute top-8 left-3 text-amber-200 text-sm">✨</div>

              {/* 用户信息 */}
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center overflow-hidden shadow-sm">
                  {avatarDataUrl ? (
                    <img src={avatarDataUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg">👶</span>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-white font-bold text-sm">{sharerNickname || '匿名用户'}</span>
                    <span className="px-1.5 py-0.5 bg-amber-300 text-amber-800 text-[10px] rounded font-bold">
                      历史最佳
                    </span>
                  </div>
                  <p className="text-white/80 text-xs mt-0.5">来听听我读绘本吧~</p>
                </div>
              </div>
            </div>

            {/* 绘本封面 */}
            <div className="px-4 pb-3">
              <div className="bg-white rounded-xl p-2.5 shadow-md">
                {storyCover && !coverError ? (
                  <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-slate-100 relative">
                    <img
                      src={coverDataUrl || storyCover}
                      alt={storyTitle}
                      className="w-full h-full object-cover"
                      onError={() => setCoverError(true)}
                    />
                  </div>
                ) : (
                  <div className="w-full aspect-[4/3] rounded-lg bg-blue-50 flex items-center justify-center">
                    <span className="text-4xl">📖</span>
                  </div>
                )}
                <p className="text-slate-800 font-bold text-sm text-center mt-2 truncate px-1">
                  {storyTitle}
                </p>
              </div>
            </div>

            {/* 二维码区域 */}
            <div className="px-4 pb-5">
              <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 flex items-center gap-3">
                <div className="w-16 h-16 bg-white rounded-lg p-1 flex-shrink-0">
                  <QRCodeSVG
                    value={fullShareUrl}
                    size={56}
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <div className="flex-1">
                  <p className="text-blue-700 font-bold text-xs">长按识别二维码</p>
                  <p className="text-blue-500 text-[10px] mt-0.5">听听我读绘本</p>
                </div>
              </div>
            </div>

            {/* 底部品牌 */}
            <div className="px-4 pb-3 text-center">
              <p className="text-white/60 text-[10px]">BadiBook · AI绘本生成器</p>
            </div>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="p-4 space-y-2">
          <button
            onClick={handleDownload}
            disabled={isGenerating}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-500 text-white font-medium hover:bg-blue-600 transition disabled:opacity-50"
          >
            <Download className="w-5 h-5" />
            {isGenerating ? '生成中...' : '保存海报到相册'}
          </button>
          <button
            onClick={handleCopyLink}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition"
          >
            {copied ? <Check className="w-5 h-5 text-green-500" /> : <Link2 className="w-5 h-5" />}
            {copied ? '链接已复制' : '复制分享链接'}
          </button>
          <p className="text-center text-xs text-slate-400">
            保存海报或复制链接分享给好友
          </p>
        </div>
      </div>
    </div>
  );
}
